import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  addDoc,
  updateDoc,
  deleteDoc,
  Timestamp,
} from 'firebase/firestore';
import { db } from '@/config/firebase';
import {
  Document,
  DocumentType,
  DocumentStatus,
  DocumentTemplate,
} from '@/models/document.model';

const DOCUMENTS_COLLECTION = 'documents';
const DOCUMENT_TEMPLATES_COLLECTION = 'documentTemplates';
const DOCUMENT_ACCESS_LOGS_COLLECTION = 'documentAccessLogs';

export class DocumentService {
  // ========== Document Management ==========

  static async getAllDocuments(): Promise<Document[]> {
    const q = query(
      collection(db, DOCUMENTS_COLLECTION),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => this.docToDocument(doc.id, doc.data()));
  }

  static async getDocumentById(id: string): Promise<Document | null> {
    const docRef = doc(db, DOCUMENTS_COLLECTION, id);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) {
      return null;
    }
    return this.docToDocument(docSnap.id, docSnap.data());
  }

  static async getDocumentsByType(type: DocumentType): Promise<Document[]> {
    const q = query(
      collection(db, DOCUMENTS_COLLECTION),
      where('type', '==', type),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => this.docToDocument(doc.id, doc.data()));
  }

  static async getDocumentsByEntity(
    entityType: string,
    entityId: string
  ): Promise<Document[]> {
    const q = query(
      collection(db, DOCUMENTS_COLLECTION),
      where('relatedEntityType', '==', entityType),
      where('relatedEntityId', '==', entityId),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => this.docToDocument(doc.id, doc.data()));
  }

  static async searchDocuments(searchTerm: string): Promise<Document[]> {
    const allDocuments = await this.getAllDocuments();
    const lowerSearch = searchTerm.toLowerCase();
    
    return allDocuments.filter(doc =>
      doc.name.toLowerCase().includes(lowerSearch) ||
      doc.description?.toLowerCase().includes(lowerSearch) ||
      doc.tags.some(tag => tag.toLowerCase().includes(lowerSearch)) ||
      doc.category?.toLowerCase().includes(lowerSearch)
    );
  }

  static async getExpiringDocuments(daysAhead: number = 30): Promise<Document[]> {
    const allDocuments = await this.getAllDocuments();
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() + daysAhead);
    
    return allDocuments.filter(doc => {
      if (!doc.expiresAt) return false;
      return doc.expiresAt <= cutoffDate && doc.expiresAt >= new Date();
    });
  }

  static async createDocument(documentData: Partial<Document>): Promise<string> {
    const now = Timestamp.now();
    const data: any = {
      name: documentData.name || '',
      description: documentData.description || null,
      type: documentData.type || DocumentType.other,
      status: documentData.status || DocumentStatus.draft,
      fileUrl: documentData.fileUrl || '',
      fileName: documentData.fileName || '',
      fileSize: documentData.fileSize || 0,
      mimeType: documentData.mimeType || '',
      category: documentData.category || null,
      tags: documentData.tags || [],
      ownerId: documentData.ownerId || '',
      ownerName: documentData.ownerName || '',
      createdBy: documentData.createdBy || '',
      createdByName: documentData.createdByName || '',
      version: documentData.version || 1,
      parentDocumentId: documentData.parentDocumentId || null,
      isLatestVersion: documentData.isLatestVersion !== undefined ? documentData.isLatestVersion : true,
      expiresAt: documentData.expiresAt ? Timestamp.fromDate(documentData.expiresAt) : null,
      reviewDate: documentData.reviewDate ? Timestamp.fromDate(documentData.reviewDate) : null,
      lastReviewedAt: documentData.lastReviewedAt ? Timestamp.fromDate(documentData.lastReviewedAt) : null,
      lastReviewedBy: documentData.lastReviewedBy || null,
      relatedEntityType: documentData.relatedEntityType || null,
      relatedEntityId: documentData.relatedEntityId || null,
      metadata: documentData.metadata || {},
      isPublic: documentData.isPublic !== undefined ? documentData.isPublic : false,
      allowedRoles: documentData.allowedRoles || [],
      allowedUserIds: documentData.allowedUserIds || [],
      createdAt: now,
      updatedAt: now,
      lastAccessedAt: null,
      accessCount: 0,
    };

    const docRef = await addDoc(collection(db, DOCUMENTS_COLLECTION), data);
    return docRef.id;
  }

  static async updateDocument(
    id: string,
    updates: Partial<Document>
  ): Promise<void> {
    const docRef = doc(db, DOCUMENTS_COLLECTION, id);
    const updateData: any = {
      updatedAt: Timestamp.now(),
    };

    if (updates.name !== undefined) updateData.name = updates.name;
    if (updates.description !== undefined) updateData.description = updates.description;
    if (updates.type !== undefined) updateData.type = updates.type;
    if (updates.status !== undefined) updateData.status = updates.status;
    if (updates.category !== undefined) updateData.category = updates.category;
    if (updates.tags !== undefined) updateData.tags = updates.tags;
    if (updates.expiresAt !== undefined) updateData.expiresAt = updates.expiresAt ? Timestamp.fromDate(updates.expiresAt) : null;
    if (updates.reviewDate !== undefined) updateData.reviewDate = updates.reviewDate ? Timestamp.fromDate(updates.reviewDate) : null;
    if (updates.lastReviewedAt !== undefined) updateData.lastReviewedAt = updates.lastReviewedAt ? Timestamp.fromDate(updates.lastReviewedAt) : null;
    if (updates.lastReviewedBy !== undefined) updateData.lastReviewedBy = updates.lastReviewedBy;
    if (updates.metadata !== undefined) updateData.metadata = updates.metadata;
    if (updates.isPublic !== undefined) updateData.isPublic = updates.isPublic;
    if (updates.allowedRoles !== undefined) updateData.allowedRoles = updates.allowedRoles;
    if (updates.allowedUserIds !== undefined) updateData.allowedUserIds = updates.allowedUserIds;

    await updateDoc(docRef, updateData);
  }

  static async deleteDocument(id: string): Promise<void> {
    const docRef = doc(db, DOCUMENTS_COLLECTION, id);
    await deleteDoc(docRef);
  }

  static async logDocumentAccess(
    documentId: string,
    userId: string,
    userName: string,
    action: 'view' | 'download' | 'edit' | 'delete' | 'share',
    ipAddress?: string,
    userAgent?: string
  ): Promise<void> {
    const now = Timestamp.now();
    const data: any = {
      documentId,
      userId,
      userName,
      action,
      ipAddress: ipAddress || null,
      userAgent: userAgent || null,
      timestamp: now,
    };

    await addDoc(collection(db, DOCUMENT_ACCESS_LOGS_COLLECTION), data);

    // Update document access count and last accessed
    const docRef = doc(db, DOCUMENTS_COLLECTION, documentId);
    await updateDoc(docRef, {
      lastAccessedAt: now,
      accessCount: (await getDoc(docRef)).data()?.accessCount || 0 + 1,
    });
  }

  // ========== Document Templates ==========

  static async getAllTemplates(): Promise<DocumentTemplate[]> {
    const q = query(
      collection(db, DOCUMENT_TEMPLATES_COLLECTION),
      where('isActive', '==', true),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => this.docToTemplate(doc.id, doc.data()));
  }

  static async getTemplateById(id: string): Promise<DocumentTemplate | null> {
    const docRef = doc(db, DOCUMENT_TEMPLATES_COLLECTION, id);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) {
      return null;
    }
    return this.docToTemplate(docSnap.id, docSnap.data());
  }

  static async createTemplate(templateData: Partial<DocumentTemplate>): Promise<string> {
    const now = Timestamp.now();
    const data: any = {
      name: templateData.name || '',
      description: templateData.description || null,
      type: templateData.type || DocumentType.other,
      templateContent: templateData.templateContent || '',
      variables: templateData.variables || [],
      category: templateData.category || null,
      isActive: templateData.isActive !== undefined ? templateData.isActive : true,
      createdBy: templateData.createdBy || '',
      createdAt: now,
      updatedAt: now,
    };

    const docRef = await addDoc(collection(db, DOCUMENT_TEMPLATES_COLLECTION), data);
    return docRef.id;
  }

  // ========== Helper Methods ==========

  private static docToDocument(id: string, data: any): Document {
    return {
      id,
      name: data.name || '',
      description: data.description,
      type: data.type || DocumentType.other,
      status: data.status || DocumentStatus.draft,
      fileUrl: data.fileUrl || '',
      fileName: data.fileName || '',
      fileSize: data.fileSize || 0,
      mimeType: data.mimeType || '',
      category: data.category,
      tags: data.tags || [],
      ownerId: data.ownerId || '',
      ownerName: data.ownerName || '',
      createdBy: data.createdBy || '',
      createdByName: data.createdByName || '',
      version: data.version || 1,
      parentDocumentId: data.parentDocumentId,
      isLatestVersion: data.isLatestVersion !== undefined ? data.isLatestVersion : true,
      expiresAt: data.expiresAt?.toDate(),
      reviewDate: data.reviewDate?.toDate(),
      lastReviewedAt: data.lastReviewedAt?.toDate(),
      lastReviewedBy: data.lastReviewedBy,
      relatedEntityType: data.relatedEntityType,
      relatedEntityId: data.relatedEntityId,
      metadata: data.metadata || {},
      isPublic: data.isPublic !== undefined ? data.isPublic : false,
      allowedRoles: data.allowedRoles || [],
      allowedUserIds: data.allowedUserIds || [],
      createdAt: data.createdAt?.toDate() || new Date(),
      updatedAt: data.updatedAt?.toDate() || new Date(),
      lastAccessedAt: data.lastAccessedAt?.toDate(),
      accessCount: data.accessCount || 0,
    };
  }

  private static docToTemplate(id: string, data: any): DocumentTemplate {
    return {
      id,
      name: data.name || '',
      description: data.description,
      type: data.type || DocumentType.other,
      templateContent: data.templateContent || '',
      variables: data.variables || [],
      category: data.category,
      isActive: data.isActive !== undefined ? data.isActive : true,
      createdBy: data.createdBy || '',
      createdAt: data.createdAt?.toDate() || new Date(),
      updatedAt: data.updatedAt?.toDate() || new Date(),
    };
  }
}

